import { useState } from "react";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { playSuccess } from "@/lib/sound";
import { EnvelopeSimple, Clock, MapPin } from "@phosphor-icons/react";

const inputCls = "w-full bg-transparent border-b-2 border-gold/30 focus:border-gold outline-none py-3 text-white placeholder:text-mist/50 transition-colors duration-300 text-sm";

export const SERVICE_OPTIONS = [
  "Patent Services",
  "Trademark Services",
  "Design Registration",
  "Copyright Services",
  "Patent Analytics",
  "Job-Work / Outsourced Support",
  "General IP Consultation",
];

export const serviceSlug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

export const ServiceChips = ({ selected, onToggle, testIdPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {SERVICE_OPTIONS.map((s) => {
      const active = selected.includes(s);
      return (
        <button type="button" key={s} onClick={() => onToggle(s)} data-testid={`${testIdPrefix}-${serviceSlug(s)}`}
          className={`text-xs px-4 py-2 border transition-colors duration-300 ${active ? "bg-gold text-navy-950 border-gold font-semibold" : "border-gold/30 text-mist hover:border-gold/70 hover:text-gold"}`}>
          {s}
        </button>
      );
    })}
  </div>
);

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", jurisdiction: "", message: "" });
  const [services, setServices] = useState(["Patent Services"]);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleService = (s) =>
    setServices((list) => (list.includes(s) ? list.filter((x) => x !== s) : [...list, s]));

  const submit = async (e) => {
    e.preventDefault();
    if (services.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, services, service: services.join(", ") });
      toast.success("Enquiry received. Our team will reply within 2 business days.");
      playSuccess();
      setForm({ name: "", email: "", company: "", jurisdiction: "", message: "" });
      setServices(["Patent Services"]);
    } catch (err) {
      toast.error(err.response
        ? formatApiErrorDetail(err.response?.data?.detail)
        : "Having trouble reaching the server — please try again in a few seconds.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-navy-900/30 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Overline>Begin your filing</Overline>
          <h2 className="font-serif font-light tracking-tight text-4xl sm:text-5xl leading-tight mb-8">
            Tell us what you've built. <span className="italic text-gold">We'll protect it.</span>
          </h2>
          <p className="text-mist leading-relaxed max-w-md mb-12">
            Share a few details about your invention, brand or creative work and the
            markets you care about. Our patent agents and IP attorneys will respond
            with a strategy and a transparent written quote.
          </p>
          <div className="flex flex-col gap-6 text-sm">
            <div className="flex items-center gap-4 text-mist">
              <EnvelopeSimple size={22} weight="duotone" className="text-gold shrink-0" />
              <span>directors@claimspacelegaltech.com</span>
            </div>
            <div className="flex items-center gap-4 text-mist">
              <Clock size={22} weight="duotone" className="text-gold shrink-0" />
              <span>Response within 2 business days</span>
            </div>
            <div className="flex items-center gap-4 text-mist">
              <MapPin size={22} weight="duotone" className="text-gold shrink-0" />
              <span>Signet 1, Unit No. 825, Premier Colony Ground, Kalyan, Thane, Mangaon — 421204</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="quote-form" className="backdrop-blur-xl bg-navy-900/40 border border-gold/20 p-8 sm:p-10 flex flex-col gap-7">
            <div className="grid sm:grid-cols-2 gap-7">
              <input required data-testid="quote-name-input" placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} />
              <input required type="email" data-testid="quote-email-input" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-7">
              <input data-testid="quote-company-input" placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} />
              <input data-testid="quote-jurisdiction-input" placeholder="Target markets (e.g. India, US, EU)" value={form.jurisdiction} onChange={set("jurisdiction")} className={inputCls} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-3">
                Services needed * <span className="text-mist/60 normal-case tracking-normal">— select all that apply</span>
              </p>
              <ServiceChips selected={services} onToggle={toggleService} testIdPrefix="quote-service-chip" />
            </div>
            <textarea required rows={4} data-testid="quote-message-input" placeholder="Briefly describe your invention, brand or work *"
              value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
            <button type="submit" disabled={sending} data-testid="quote-submit-button"
              className="mt-2 px-8 py-4 bg-gold text-navy-950 font-semibold text-sm tracking-wide hover:bg-gold-light transition-colors duration-300 disabled:opacity-50">
              {sending ? "Sending..." : "Request a Quote"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
